// src/components/EisenhowerMatrixView.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useOutletContext, useNavigate, useParams } from 'react-router-dom';
import { formatDate } from '../utils';
import { ROUTES, INTERNAL_CLIENT_ID } from '../constants/routes';
import {
    RiInformationLine as InformationCircleIcon,
    RiQuestionLine as QuestionMarkCircleIcon,
    RiSubtractLine,
    RiAddLine
} from 'react-icons/ri';
import { ScoreTooltip } from './ScoreTooltips';
import { useStaffContext } from '../contexts/StaffContext';

// Apollo-style design tokens
const apolloStyles = {
    transition: {
        fast: 'all 0.15s ease-in-out',
        normal: 'all 0.2s ease',
    },
    borderRadius: {
        sm: 'var(--pallium-border-radius-sm, 2px)',
        default: 'var(--pallium-border-radius, 4px)',
        md: 'var(--pallium-border-radius-md, 6px)',
        lg: 'var(--pallium-border-radius-lg, 8px)',
        xl: '12px',
    },
    shadow: {
        xs: 'var(--pallium-shadow-xs)',
        sm: 'var(--pallium-shadow-sm)',
        md: 'var(--pallium-shadow-md)',
    },
    focusRing: 'var(--pallium-focus-ring-primary, 0 0 0 0.25rem rgba(110, 79, 196, 0.25))',
};

// Quadrant colors - softer Apollo-style approach with subtle accents
const quadrantStyles = {
    do: {
        accent: 'var(--pallium-error-red)',
        headerBg: 'color-mix(in srgb, var(--pallium-error-red) 8%, var(--pallium-bg-primary))',
        headerText: 'var(--pallium-error-red)',
        lightBg: 'var(--pallium-bg-primary)',
        countBg: 'color-mix(in srgb, var(--pallium-error-red) 15%, transparent)',
    },
    schedule: {
        accent: 'var(--pallium-info-cyan)',
        headerBg: 'color-mix(in srgb, var(--pallium-info-cyan) 8%, var(--pallium-bg-primary))',
        headerText: 'var(--pallium-info-cyan)',
        lightBg: 'var(--pallium-bg-primary)',
        countBg: 'color-mix(in srgb, var(--pallium-info-cyan) 15%, transparent)',
    },
    delegate: {
        accent: 'var(--pallium-warning-yellow)',
        headerBg: 'color-mix(in srgb, var(--pallium-warning-yellow) 8%, var(--pallium-bg-primary))',
        headerText: 'var(--pallium-warning-yellow)',
        lightBg: 'var(--pallium-bg-primary)',
        countBg: 'color-mix(in srgb, var(--pallium-warning-yellow) 15%, transparent)',
    },
    eliminate: {
        accent: 'var(--pallium-mute-text)',
        headerBg: 'var(--pallium-bg-secondary)',
        headerText: 'var(--pallium-text-secondary)',
        lightBg: 'var(--pallium-bg-primary)',
        countBg: 'color-mix(in srgb, var(--pallium-mute-text) 15%, transparent)',
    },
};

// Item type badge colors
const typeBadgeStyles = {
    Goal: {
        bg: 'color-mix(in srgb, var(--pallium-info-cyan) 15%, transparent)',
        color: 'var(--pallium-info-cyan)',
    },
    Initiative: {
        bg: 'var(--pallium-selected-option-bg, var(--pallium-purple-0-5))',
        color: 'var(--pallium-primary-purple)',
    },
    Issue: {
        bg: 'color-mix(in srgb, var(--pallium-error-red) 15%, transparent)',
        color: 'var(--pallium-error-red)',
    },
    ActionItem: {
        bg: 'color-mix(in srgb, var(--pallium-success-green) 15%, transparent)',
        color: 'var(--pallium-success-green)',
    },
};

// This function takes quadrant-specific thresholds
const getQuadrant = (importance, urgency, quadrantImportanceThreshold, quadrantUrgencyThreshold) => {
    const isImportant = importance >= quadrantImportanceThreshold;
    const isUrgent = urgency >= quadrantUrgencyThreshold;

    if (isUrgent && isImportant) return 'do';
    if (!isUrgent && isImportant) return 'schedule';
    if (isUrgent && !isImportant) return 'delegate';
    return 'eliminate';
};

// Tooltip Portal - renders tooltip at document body level to avoid clipping by overflow:hidden
const TooltipPortal = ({ children, triggerRef, isVisible }) => {
    const [position, setPosition] = useState({ top: 0, left: 0 });
    
    useEffect(() => {
        if (isVisible && triggerRef.current) {
            const rect = triggerRef.current.getBoundingClientRect();
            const tooltipWidth = 288; // w-72 = 18rem = 288px
            
            const viewportWidth = document.documentElement.clientWidth;
            
            // Position below the trigger, centered
            let left = rect.left + rect.width / 2 - tooltipWidth / 2;
            let top = rect.bottom + 8;
            
            // Keep within viewport bounds with 12px padding
            if (left < 12) left = 12;
            if (left + tooltipWidth > viewportWidth - 12) {
                left = viewportWidth - tooltipWidth - 12;
            }
            
            setPosition({ top, left });
        }
    }, [isVisible, triggerRef]);
    
    if (!isVisible) return null;
    
    return ReactDOM.createPortal(
        <div style={{
            position: 'fixed',
            top: position.top,
            left: position.left,
            zIndex: 9999,
        }}>
            {children}
        </div>,
        document.body
    );
};

// Tooltip component with Apollo styling - uses portal to escape overflow:hidden containers
const Tooltip = ({ content, children }) => {
    const [isVisible, setIsVisible] = useState(false);
    const triggerRef = useRef(null);
    
    return (
        <span 
            ref={triggerRef}
            className="inline-flex items-center cursor-help"
            onMouseEnter={() => setIsVisible(true)}
            onMouseLeave={() => setIsVisible(false)}
            onClick={() => setIsVisible(!isVisible)}
        >
            {children}
            <TooltipPortal triggerRef={triggerRef} isVisible={isVisible}>
                <div 
                    className="w-72 p-3 text-sm"
                    style={{
                        backgroundColor: 'var(--pallium-primary-dark-navy)',
                        color: 'var(--pallium-white)',
                        borderRadius: apolloStyles.borderRadius.lg,
                        boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
                    }}
                >
                    {content}
                    <div 
                        className="absolute -top-1.5 left-1/2 -translate-x-1/2 w-0 h-0"
                        style={{
                            borderLeft: '6px solid transparent',
                            borderRight: '6px solid transparent',
                            borderBottom: '6px solid var(--pallium-primary-dark-navy)',
                        }}
                    />
                </div>
            </TooltipPortal>
        </span>
    );
};

// Stepper input component with Apollo styling
const StepperInput = ({ value, onChange, min, max, step, label, color, tooltip, suffix }) => {
    const handleDecrement = () => {
        const newValue = Math.max(min, value - step);
        onChange(newValue);
    };
    
    const handleIncrement = () => {
        const newValue = Math.min(max, value + step);
        onChange(newValue);
    };
    
    const displayValue = step < 1 ? value.toFixed(1) : value;
    const isActive = value > min;
    
    return (
        <div className="flex items-center gap-1.5">
            <span className="text-xs" style={{ color: 'var(--pallium-text-secondary)' }}>{label}</span>
            {tooltip && (
                <Tooltip content={tooltip}>
                    <InformationCircleIcon className="h-3 w-3" style={{ color: 'var(--pallium-icon-gray)' }} />
                </Tooltip>
            )}
            <div 
                className="flex items-center overflow-hidden"
                style={{
                    border: `1px solid ${isActive ? 'var(--pallium-purple-2)' : 'var(--pallium-border-default)'}`,
                    backgroundColor: isActive ? 'var(--pallium-selected-option-bg, var(--pallium-purple-0-5))' : 'var(--pallium-bg-primary)',
                    borderRadius: apolloStyles.borderRadius.default,
                    transition: apolloStyles.transition.fast,
                }}
            >
                <button
                    onClick={handleDecrement}
                    disabled={value <= min}
                    className="px-1.5 py-0.5 disabled:opacity-40 disabled:cursor-not-allowed"
                    style={{ 
                        transition: apolloStyles.transition.fast,
                        border: 'none',
                        background: 'transparent',
                    }}
                    onMouseOver={(e) => { if (value > min) e.currentTarget.style.backgroundColor = 'var(--pallium-bg-secondary)'; }}
                    onMouseOut={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                >
                    <RiSubtractLine className="h-3 w-3" style={{ color: 'var(--pallium-text-secondary)' }} />
                </button>
                <span 
                    className="px-1.5 py-0.5 font-semibold text-xs min-w-[28px] text-center"
                    style={{ 
                        color: isActive ? 'var(--pallium-primary-purple)' : 'var(--pallium-text-secondary)',
                        borderLeft: '1px solid var(--pallium-border-default)',
                        borderRight: '1px solid var(--pallium-border-default)',
                    }}
                >
                    {displayValue}
                </span>
                <button
                    onClick={handleIncrement}
                    disabled={value >= max}
                    className="px-1.5 py-0.5 disabled:opacity-40 disabled:cursor-not-allowed"
                    style={{ 
                        transition: apolloStyles.transition.fast,
                        border: 'none',
                        background: 'transparent',
                    }}
                    onMouseOver={(e) => { if (value < max) e.currentTarget.style.backgroundColor = 'var(--pallium-bg-secondary)'; }}
                    onMouseOut={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                >
                    <RiAddLine className="h-3 w-3" style={{ color: 'var(--pallium-text-secondary)' }} />
                </button>
            </div>
            {suffix && <span className="text-xs" style={{ color: 'var(--pallium-text-secondary)' }}>{suffix}</span>}
        </div>
    );
};

const Quadrant = ({ title, items, quadrantKey, count, onCardClick, getDriDisplayName }) => {
    const styles = quadrantStyles[quadrantKey];
    
    return (
        <div 
            className="overflow-hidden flex flex-col"
            style={{
                backgroundColor: styles.lightBg,
                border: '1px solid var(--pallium-border-default)',
                borderRadius: apolloStyles.borderRadius.xl,
                boxShadow: apolloStyles.shadow.sm,
            }}
        >
            {/* Header - softer with accent color text */}
            <div 
                className="px-3 py-2 flex items-center justify-between"
                style={{ 
                    backgroundColor: styles.headerBg,
                    borderBottom: '1px solid var(--pallium-border-subtle)',
                }}
            >
                <div className="flex items-center gap-2">
                    <div 
                        className="w-2 h-2 rounded-full"
                        style={{ backgroundColor: styles.accent }}
                    />
                    <h3 className="text-xs font-semibold uppercase tracking-wide" style={{ color: styles.headerText }}>{title}</h3>
                </div>
                <span 
                    className="text-[10px] font-bold px-1.5 py-0.5 min-w-[20px] text-center"
                    style={{ 
                        color: styles.headerText,
                        backgroundColor: styles.countBg,
                        borderRadius: apolloStyles.borderRadius.default,
                    }}
                >
                    {count}
                </span>
            </div>
            
            {/* Content */}
            <div 
                className="flex-1 p-2 min-h-[160px] max-h-[280px] overflow-y-auto"
                style={{ backgroundColor: 'var(--pallium-bg-secondary)' }}
            >
                {items.length === 0 ? (
                    <div 
                        className="h-full flex items-center justify-center"
                        style={{ minHeight: '140px' }}
                    >
                        <p 
                            className="text-xs"
                            style={{ color: 'var(--pallium-text-disabled)' }}
                        >
                            No items
                        </p>
                    </div>
                ) : (
                    <div className="space-y-1.5">
                        {items.map(item => {
                            const badge = typeBadgeStyles[item.type] || { bg: 'var(--pallium-bg-secondary)', color: 'var(--pallium-text-secondary)' };
                            return (
                                <div
                                    key={item.id}
                                    className="p-2 cursor-pointer"
                                    style={{
                                        backgroundColor: 'var(--pallium-bg-primary)',
                                        border: '1px solid var(--pallium-border-subtle)',
                                        borderRadius: apolloStyles.borderRadius.md,
                                        transition: apolloStyles.transition.fast,
                                    }}
                                    onClick={() => onCardClick(item)}
                                    title={`Click to view details for ${item.name}`}
                                    onMouseOver={(e) => {
                                        e.currentTarget.style.boxShadow = apolloStyles.shadow.xs;
                                        e.currentTarget.style.borderColor = styles.accent;
                                    }}
                                    onMouseOut={(e) => {
                                        e.currentTarget.style.boxShadow = 'none';
                                        e.currentTarget.style.borderColor = 'var(--pallium-border-subtle)';
                                    }}
                                >
                                    <div className="flex justify-between items-start gap-2">
                                        <p 
                                            className="font-medium text-xs leading-snug line-clamp-2 flex-1"
                                            style={{ color: 'var(--pallium-text-primary)' }}
                                        >
                                            {item.name}
                                        </p>
                                        <span 
                                            className="text-[9px] px-1 py-0.5 font-medium shrink-0"
                                            style={{ 
                                                backgroundColor: badge.bg, 
                                                color: badge.color,
                                                borderRadius: apolloStyles.borderRadius.sm,
                                            }}
                                        >
                                            {item.type === 'ActionItem' ? 'Action' : item.type}
                                        </span>
                                    </div>
                                    <div className="text-[10px] mt-1 space-y-0.5" style={{ color: 'var(--pallium-text-secondary)' }}>
                                        {item.detail1 && (
                                            <div className="truncate">
                                                {item.type === 'ActionItem' ? `Assigned: ${getDriDisplayName(item.detail1)}` :
                                                 item.type === 'Issue' ? `Severity: ${item.detail1}` :
                                                 `Status: ${item.detail1}`}
                                            </div>
                                        )}
                                        {item.detail2 && (
                                            <div className="truncate">
                                                {item.type === 'ActionItem' ? `Due: ${formatDate(item.detail2)}` :
                                                 item.type === 'Goal' ? `Target: ${formatDate(item.detail2)}` :
                                                 ''}
                                            </div>
                                        )}
                                        {item.relatedClients && item.relatedClients.length > 0 && (
                                            <div className="truncate" style={{ color: 'var(--pallium-text-disabled)' }}>
                                                Client: {item.relatedClients.join(', ')}
                                            </div>
                                        )}
                                    </div>
                                    <div 
                                        className="text-[9px] mt-1.5 pt-1 flex justify-between items-center"
                                        style={{ 
                                            color: 'var(--pallium-text-disabled)',
                                            borderTop: '1px solid var(--pallium-border-subtle)',
                                        }}
                                    >
                                        <span className="flex items-center gap-0.5">
                                            Imp: {item.importanceScore}
                                            <ScoreTooltip type="importance" size="sm" />
                                        </span>
                                        <span className="flex items-center gap-0.5">
                                            Urg: {item.urgencyScore.toFixed(1)}
                                            <ScoreTooltip type="urgency" entityType={item.type} size="sm" />
                                        </span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
};

const EisenhowerMatrixView = ({ 
    departmentId: propDepartmentId, 
    clientId: propClientId, 
    typeFilter: propTypeFilter = ['Goal', 'Initiative', 'ActionItem', 'Issue'], 
    driFilter: propDriFilter,
    clientFilter: propClientFilter,
    minImportanceFilter = 0,
    minUrgencyFilter = 0,
    // Entity-specific filters
    goalStatusFilter = '',
    initiativeStatusFilter = '',
    issueSeverityFilter = '',
    issueStatusFilter = '',
    deadlineFilter = '',  // 'overdue' or empty
    actionInitiativeFilter = '',
    assigneeFilter = '',  // Filter for ActionItem assignees
    showBlockedInitiatives = false,
    actionItemStatusFilter = ''  // Filter for ActionItem status
}) => {
    const outletContext = useOutletContext();
    const params = useParams();
    const { getDriDisplayName } = useStaffContext();

    const apiService = outletContext?.apiService;
    const departmentId = propDepartmentId || outletContext?.departmentId || params.departmentId;
    const clientId = propClientId || outletContext?.clientId || params.clientId;
    const dateRange = outletContext?.dateRange;
    
    const initialTypeFilter = Array.isArray(propTypeFilter) ? propTypeFilter : (propTypeFilter === 'all' ? ['Goal', 'Initiative', 'ActionItem', 'Issue'] : [propTypeFilter]);


    const navigate = useNavigate();
    const [matrixData, setMatrixData] = useState({ do: [], schedule: [], delegate: [], eliminate: [] });
    const [allItems, setAllItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [quadrantImportanceThreshold, setQuadrantImportanceThreshold] = useState(3);
    const [quadrantUrgencyThreshold, setQuadrantUrgencyThreshold] = useState(2);

    const [displayedItemCount, setDisplayedItemCount] = useState(0);
    const [localTypeFilter, setLocalTypeFilter] = useState(initialTypeFilter);


    // Load data once per client/department and date range - cache it and filter locally
    const loadMatrixData = useCallback(async () => {
        if (!apiService || (!clientId && !departmentId) || !dateRange || !dateRange.start || !dateRange.end) {
            setError("Missing required context (API service, ID, or date range)");
            setLoading(false);
            return;
        }
        setLoading(true);
        setError(null);
        try {
            // Fetch data with client filter if provided
            const filters = {
                startDate: dateRange.start,
                endDate: dateRange.end,
                clientFilter: propClientFilter && propClientFilter.length > 0 ? propClientFilter : undefined,
            };
            
            let results;
            if (clientId) {
                results = await apiService.getEisenhowerData(clientId, filters);
            } else if (departmentId) {
                results = await apiService.getEisenhowerData(null, { ...filters, departmentId });
            } else {
                throw new Error("Neither clientId nor departmentId provided");
            }
            
            setAllItems(results || []);
        } catch (err) {
            console.error("Error loading Eisenhower Matrix data:", err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [apiService, clientId, departmentId, dateRange, propClientFilter]);

    useEffect(() => {
        loadMatrixData();
    }, [loadMatrixData]);

    // Local filtering - doesn't trigger API reload
    useEffect(() => {
        const currentTypeFilters = localTypeFilter;
        const today = new Date().toISOString().split('T')[0];

        // Filter by type
        let filteredItems = allItems.filter(item =>
            currentTypeFilters.includes(item.type)
        );
        
        // Filter by DRI if specified (for ActionItems, detail1 is assigned_to)
        if (propDriFilter && propDriFilter.length > 0) {
            filteredItems = filteredItems.filter(item => {
                // For ActionItems, DRI is in detail1 (assigned_to)
                // For other types, check the dri field
                if (item.type === 'ActionItem') {
                    return item.detail1 && propDriFilter.includes(item.detail1);
                }
                return item.dri && propDriFilter.includes(item.dri);
            });
        }

        // Apply entity-specific filters
        // Goal status filter (detail1 contains status for Goals)
        if (goalStatusFilter) {
            filteredItems = filteredItems.filter(item => 
                item.type !== 'Goal' || item.detail1 === goalStatusFilter
            );
        }
        
        // Initiative status filter (detail1 contains status for Initiatives)
        if (initiativeStatusFilter) {
            filteredItems = filteredItems.filter(item => 
                item.type !== 'Initiative' || item.detail1 === initiativeStatusFilter
            );
        }
        
        // Show blocked initiatives only (open initiatives with blocking issues)
        if (showBlockedInitiatives) {
            filteredItems = filteredItems.filter(item => 
                item.type !== 'Initiative' || (item.detail1 === 'open' && item.blockingIssuesCount > 0)
            );
        }
        
        // Issue severity filter (detail1 contains severity for Issues)
        if (issueSeverityFilter) {
            filteredItems = filteredItems.filter(item => 
                item.type !== 'Issue' || item.detail1 === issueSeverityFilter
            );
        }
        
        // Issue status filter (issueStatus field from backend)
        if (issueStatusFilter) {
            filteredItems = filteredItems.filter(item => 
                item.type !== 'Issue' || item.issueStatus === issueStatusFilter
            );
        }
        
        // Action item initiative filter (initiativeName field from backend)
        if (actionInitiativeFilter) {
            filteredItems = filteredItems.filter(item => 
                item.type !== 'ActionItem' || item.initiativeName === actionInitiativeFilter
            );
        }
        
        // Deadline filter for ActionItems (detail2 contains deadline)
        if (deadlineFilter === 'overdue') {
            filteredItems = filteredItems.filter(item => {
                if (item.type !== 'ActionItem') return true;
                if (!item.detail2) return false; // No deadline means not overdue
                return item.detail2 < today;
            });
        }
        
        // Assignee filter for ActionItems (detail1 contains assigned_to)
        if (assigneeFilter) {
            filteredItems = filteredItems.filter(item => 
                item.type !== 'ActionItem' || item.detail1 === assigneeFilter
            );
        }
        
        // Action item status filter (actionItemStatus field from backend)
        if (actionItemStatusFilter) {
            filteredItems = filteredItems.filter(item => 
                item.type !== 'ActionItem' || item.actionItemStatus === actionItemStatusFilter
            );
        }

        // Filter by min importance and urgency
        const valueFilteredItems = filteredItems.filter(item =>
            item.importanceScore >= minImportanceFilter &&
            item.urgencyScore >= minUrgencyFilter
        );

        setDisplayedItemCount(valueFilteredItems.length);

        if (valueFilteredItems.length > 0) {
            const categorizedData = { do: [], schedule: [], delegate: [], eliminate: [] };
            valueFilteredItems.forEach(item => {
                const quadrant = getQuadrant(
                    item.importanceScore,
                    item.urgencyScore,
                    quadrantImportanceThreshold,
                    quadrantUrgencyThreshold
                );
                categorizedData[quadrant].push(item);
            });
            setMatrixData(categorizedData);
        } else {
            setMatrixData({ do: [], schedule: [], delegate: [], eliminate: [] });
        }
    }, [allItems, localTypeFilter, minImportanceFilter, minUrgencyFilter, quadrantImportanceThreshold, quadrantUrgencyThreshold, propDriFilter, goalStatusFilter, initiativeStatusFilter, issueSeverityFilter, issueStatusFilter, deadlineFilter, actionInitiativeFilter, assigneeFilter, showBlockedInitiatives, actionItemStatusFilter]);
    
    useEffect(() => {
        const newFilterArray = Array.isArray(propTypeFilter) ? propTypeFilter : (propTypeFilter === 'all' ? ['Goal', 'Initiative', 'ActionItem', 'Issue'] : [propTypeFilter]);
        setLocalTypeFilter(newFilterArray);
    }, [propTypeFilter]);


    const handleCardClick = (item) => {
        let entityTypePath = '';

        switch(item.type) {
            case 'Goal': entityTypePath = 'goals'; break;
            case 'Initiative': entityTypePath = 'initiatives'; break;
            case 'ActionItem': entityTypePath = 'action-items'; break;
            case 'Issue': entityTypePath = 'issues'; break;
            default: console.warn('Unknown entity type for navigation:', item.type); return;
        }

        if (item.clientId && entityTypePath) {
            navigate(`${ROUTES.ACCOUNTS}/${item.clientId}/${entityTypePath}/${item.id}`);
        } else if (clientId && entityTypePath) {
            navigate(`${ROUTES.ACCOUNTS}/${clientId}/${entityTypePath}/${item.id}`);
        } else if (departmentId && entityTypePath) {
            console.info(`Item ${item.id} has no clientId, navigating within department context`);
            navigate(`${ROUTES.OPERATIONS}/${departmentId}/${entityTypePath}/${item.id}`);
        } else if (entityTypePath) {
            console.info(`Item ${item.id} has no context, using internal client as fallback`);
            navigate(`${ROUTES.ACCOUNTS}/${INTERNAL_CLIENT_ID}/${entityTypePath}/${item.id}`);
        } else {
            console.warn(`Cannot determine entity type path for item ${item.id}`);
        }
    };

    if (error) return (
        <div 
            className="p-4 min-h-[400px] flex items-center"
            style={{
                backgroundColor: 'var(--pallium-alert-error-bg, rgba(252, 57, 57, 0.1))',
                borderLeft: '4px solid var(--pallium-error-red)',
                color: 'var(--pallium-error-red)',
                borderRadius: apolloStyles.borderRadius.default,
            }}
        >
            Error: {error}
        </div>
    );

    const LoadingOverlay = () => loading ? (
        <div 
            className="absolute inset-0 flex items-center justify-center z-10"
            style={{
                backgroundColor: 'color-mix(in srgb, var(--pallium-bg-primary) 70%, transparent)',
                borderRadius: apolloStyles.borderRadius.lg,
            }}
        >
            <div className="flex items-center">
                <div 
                    className="animate-spin rounded-full h-6 w-6 border-b-2"
                    style={{ borderColor: 'var(--pallium-primary-purple)' }}
                />
                <span className="ml-2 text-sm" style={{ color: 'var(--pallium-text-secondary)' }}>Loading...</span>
            </div>
        </div>
    ) : null;

    const totalItems = allItems.length;
    const quadrantCounts = {
        do: matrixData.do.length,
        schedule: matrixData.schedule.length,
        delegate: matrixData.delegate.length,
        eliminate: matrixData.eliminate.length
    };

    return (
        <div className="relative">
            <LoadingOverlay />
            
            {/* Compact inline header with item count and threshold controls */}
            <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                <div className="flex items-center gap-2">
                    <span className="text-xs" style={{ color: 'var(--pallium-text-secondary)' }}>
                        <span className="font-semibold" style={{ color: 'var(--pallium-text-primary)' }}>{displayedItemCount}</span> items
                        {totalItems > displayedItemCount && (
                            <span className="ml-1" style={{ color: 'var(--pallium-text-disabled)' }}>(of {totalItems})</span>
                        )}
                    </span>
                    <Tooltip content={
                        <div>
                            <p className="font-medium mb-1">The Eisenhower Matrix</p>
                            <p>Categorizes items based on importance (connections) and urgency (time sensitivity).</p>
                        </div>
                    }>
                        <QuestionMarkCircleIcon className="h-3.5 w-3.5" style={{ color: 'var(--pallium-icon-gray)' }} />
                    </Tooltip>
                </div>
                
                {/* Inline threshold controls */}
                <div className="flex items-center gap-3">
                    <StepperInput
                        value={quadrantImportanceThreshold}
                        onChange={setQuadrantImportanceThreshold}
                        min={0}
                        max={10}
                        step={1}
                        label="Important if >"
                        tooltip="Importance = number of connections (linked goals, initiatives, issues, action items). More connections = higher impact."
                    />
                    <StepperInput
                        value={quadrantUrgencyThreshold}
                        onChange={setQuadrantUrgencyThreshold}
                        min={0}
                        max={3}
                        step={0.5}
                        label="Urgent if >"
                        tooltip="Urgency varies by type: Issues use severity, Action Items use deadline proximity, Goals use target date, Initiatives use blocked status."
                    />
                </div>
            </div>

            {displayedItemCount === 0 && !loading ? (
                <div 
                    className="p-8 text-xs text-center"
                    style={{
                        backgroundColor: 'var(--pallium-bg-secondary)',
                        border: '1px solid var(--pallium-border-subtle)',
                        borderRadius: apolloStyles.borderRadius.xl,
                        color: 'var(--pallium-text-disabled)',
                    }}
                >
                    No items match current filters
                </div>
            ) : (
                /* Matrix Grid */
                <div className="grid grid-cols-2 gap-2.5">
                    <Quadrant
                        title="Do First"
                        items={matrixData.do}
                        quadrantKey="do"
                        count={quadrantCounts.do}
                        onCardClick={handleCardClick}
                        getDriDisplayName={getDriDisplayName}
                    />
                    <Quadrant
                        title="Schedule"
                        items={matrixData.schedule}
                        quadrantKey="schedule"
                        count={quadrantCounts.schedule}
                        onCardClick={handleCardClick}
                        getDriDisplayName={getDriDisplayName}
                    />
                    <Quadrant
                        title="Delegate"
                        items={matrixData.delegate}
                        quadrantKey="delegate"
                        count={quadrantCounts.delegate}
                        onCardClick={handleCardClick}
                        getDriDisplayName={getDriDisplayName}
                    />
                    <Quadrant
                        title="Eliminate/Delay"
                        items={matrixData.eliminate}
                        quadrantKey="eliminate"
                        getDriDisplayName={getDriDisplayName}
                        count={quadrantCounts.eliminate}
                        onCardClick={handleCardClick}
                    />
                </div>
            )}
        </div>
    );
};

export default EisenhowerMatrixView;
